/**
 * Parent FAQ (KidHive). Plain-language help for parents, rendered by FaqPage with the same
 * LegalContent block renderer the legal pages use. Client-authored copy; edit the text here.
 * A closing `note` points to the governing legal documents.
 */
const content = [
  { type: 'h2', text: 'What is KidHive?' },
  {
    type: 'p',
    text:
      'KidHive is one trusted place for parents to discover, compare, and book local camps, classes, and activities for their kids — no more hunting across Facebook groups, Instagram, and outdated flyers. Parents browse by age, location, and interest, then book and pay in a few taps.',
  },

  { type: 'h2', text: 'Where is KidHive available?' },
  {
    type: 'p',
    text:
      'KidHive is currently live in Los Angeles and Orange County. Outside that area, parents can sign up to register their interest and KidHive will email them the moment it launches in their city.',
  },

  { type: 'h2', text: 'Is KidHive free to use?' },
  {
    type: 'p',
    text:
      'Yes. Joining and browsing is completely free for parents — parents only pay for the activities they book.',
  },

  { type: 'h2', text: 'How does a parent book a class, camp, or activity?' },
  {
    type: 'p',
    text:
      "Parents search all classes in and around their home or current location, filter by their child's age, location, and category, then pick a session — a single drop-in, a camp, or a full-term enrollment. Parents can add multiple children to their account and choose who's attending. A confirmation and reminders arrive by email so parents can plan accordingly and never miss a booking.",
  },

  { type: 'h2', text: 'How far in advance can a class be booked?' },
  {
    type: 'p',
    text:
      "Up to 6 months. For camps and multi-week classes, the 6 months is measured to the program's first session, so it doesn't limit how long a program runs — only how far ahead a parent can book and pay. Dates further out simply don't appear as bookable.",
  },

  { type: 'h2', text: 'What payment methods are accepted, and who is the parent paying?' },
  {
    type: 'p',
    text:
      'Parents can pay with Apple Pay, Google Pay, or a card through Stripe. The price shown is the price paid. Payment goes to the provider running the activity — KidHive facilitates the transaction through Stripe but never holds the money.',
  },

  { type: 'h2', text: 'What is the cancellation and refund policy?' },
  {
    type: 'p',
    text:
      "Single sessions: 100% refund 24+ hours out, 50% between 12–24 hours, no refund under 12 hours. Camps and full-term enrollments: 100% refund any time before the first session, none after. Refunds are automatic to the parent's original payment method. Some classes charge a one-time per-child registration fee, shown on the class page — it applies only the first time a child books that class, and if the booking is cancelled or refunded it isn't used up and won't be charged again on a rebooking.",
  },

  { type: 'h2', text: 'Can a parent reschedule instead of canceling?' },
  {
    type: 'p',
    text:
      "For single sessions, yes — a parent can move to another session of the same class if still 24+ hours out. Outside that window, cancel and rebook instead. Camps and full-term enrollments can't be rescheduled at this time.",
  },

  {
    type: 'h2',
    text: 'Can individual days in a camp or multi-week class be canceled or rescheduled?',
  },
  {
    type: 'p',
    text:
      "Not at this time — canceling or rescheduling individual days within a camp or multi-week class isn't currently supported, for either parents or providers. If an entire camp or multi-week class the parent has already booked is canceled, the parent is refunded in full. For a single affected day, the parent and the provider can exchange messages on KidHive and agree on a make-up session, if both agree. KidHive understands this can be inconvenient in the platform's current state, and will introduce more flexible capabilities as KidHive grows and improves over time.",
  },

  { type: 'h2', text: 'What happens if a provider cancels or reschedules a booking?' },
  {
    type: 'p',
    text:
      "If a provider cancels, the parent gets a 100% refund automatically, no matter the timing — and KidHive's platform fee is refunded as well. If a provider reschedules a single drop-in session, the parent is notified of the proposed new date/time and can accept or decline: accept, and no money moves — the booking simply transfers to the new date/time; decline, and the parent is fully refunded. If KidHive ever has to cancel a class — for example because of a provider suspension, or on behalf of the provider — the parent also receives a 100% refund.",
  },

  {
    type: 'h2',
    text: "Is the parent responsible for Stripe's processing fee on refunds and cancellations?",
  },
  {
    type: 'p',
    text:
      "No. Parents never pay the processing fee on a refund — refunds go back to the original payment method per the policy above. The Stripe processing fee on a refunded booking is always the provider's responsibility, no matter who cancels.",
  },

  { type: 'h2', text: 'Does KidHive vet or guarantee the providers on the platform?' },
  {
    type: 'p',
    text:
      "Providers are hand-picked and reviewed before they're listed, but that's not an endorsement — KidHive doesn't independently verify every credential or insurance claim. KidHive provides parents with all the information it has access to, so decision-making is easier and faster.",
  },

  {
    type: 'h2',
    text: 'What information does KidHive collect about a child, and how is it protected?',
  },
  {
    type: 'p',
    text:
      "Just a first name and birth year, used to match age-appropriate activities and shared only with the provider the parent books. It's never used for advertising or sold, and it's covered by COPPA — parents can email info@KidHive.co any time to review or delete it, and can also delete their profile at any time via the profile page.",
  },

  { type: 'h2', text: "Who makes sure a provider knows about a child's allergies?" },
  {
    type: 'p',
    text:
      "The parent does. Parents record any allergies in their child's profile, and they're passed to the provider with the booking. But KidHive can't guarantee a provider has read or acted on them — so on arrival, the parent should confirm directly with the provider that they're aware of any allergies. Providers are also expected to ask. KidHive passes allergy information along as a convenience but is not responsible for whether a provider acknowledges or acts on it.",
  },

  { type: 'h2', text: 'Will parents need to sign any waivers?' },
  {
    type: 'p',
    text:
      "Some providers ask families to sign a waiver or other document as a condition of participation. Any such paperwork is between the parent and the provider, handled when the family arrives for the class — KidHive doesn't provide or store these documents and isn't responsible for them.",
  },

  { type: 'h2', text: 'What if a parent and provider disagree about a class?' },
  {
    type: 'p',
    text:
      "The class is delivered under a contract between the parent and the provider, so any disagreement — before, during, or after it — is theirs to resolve. If it's a payment or booking problem, KidHive will help in good faith and share the booking records it has, but KidHive doesn't arbitrate disputes, take sides, or accept responsibility for the activity itself or its outcome. Parents and providers are encouraged to resolve issues directly.",
  },

  {
    type: 'note',
    text:
      "This FAQ is a plain-language summary — it doesn't replace KidHive's Terms of Service, Provider Agreement, Cancellation & Refund Policy, or Privacy Policy, which govern if there's ever a difference. Questions about anything here? Email info@KidHive.co.",
  },
];

export default content;
